import axios from 'axios'
import * as HttpStatus from 'http-status-codes'
import { addNoonToDate } from 'shared/helpers/formatters'
import { PaginatedResults } from 'shared/helpers/typeHelper'

interface UploadTenstreetDocument {
  accidents: number
  violations: number
  jobs: number
}

export function uploadTenstreetDocument(
  userId: string,
  document: FormData
): Promise<UploadTenstreetDocument> {
  const url = `/api/v3/users/${userId}/tenstreet-document/`
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }

  return axios.post(url, document, config).then((response) => {
    return response.data
  })
}

export enum DrivingViolationType {
  RECKLESS_DRIVING = 'reckless_driving',
  DUI_OWI = 'dui_owi',
  OBEY_SIGNAL = 'obey_signal',
  LANE_CHANGE = 'lane_change',
  SEAT_BELT = 'seat_belt',
  SPEEDING = 'speeding',
  SUSPENDED_LICENSE = 'suspended_license',
  YIELD = 'yield',
  PASSING_TURN_BREAK = 'passing_turn_break',
  NO_INSURANCE = 'no_insurance',
  CELL_PHONE = 'cell_phone',
  OTHER = 'other',
}

export const DrivingViolationTypeDetail = new Map<DrivingViolationType, string>([
  [DrivingViolationType.RECKLESS_DRIVING, 'Careless/reckless driving'],
  [DrivingViolationType.DUI_OWI, 'DUI/OWI'],
  [DrivingViolationType.OBEY_SIGNAL, 'Failure to obey signal'],
  [DrivingViolationType.LANE_CHANGE, 'Improper lane change'],
  [DrivingViolationType.SEAT_BELT, 'Seat belt'],
  [DrivingViolationType.SPEEDING, 'Speeding'],
  [DrivingViolationType.SUSPENDED_LICENSE, 'Driving with suspended license'],
  [DrivingViolationType.YIELD, 'Failure to yield'],
  [DrivingViolationType.PASSING_TURN_BREAK, 'Improper passing/turn/braking'],
  [DrivingViolationType.NO_INSURANCE, 'No insurance / proof of insurance'],
  [DrivingViolationType.CELL_PHONE, 'Cell phone usage'],
  [DrivingViolationType.OTHER, 'Other'],
])

export const getDrivingViolationTypeDetailValues = () =>
  Array.from(DrivingViolationTypeDetail.entries()).map((p) => ({
    label: p[1],
    value: p[0],
  }))

export interface DrivingViolation {
  id: string
  dateCreated: string
  dateUpdated: string
  address: string
  date: string
  dateFormatted: string
  isInCommercialVehicle: boolean
  violationType: DrivingViolationType
  violationTypeDetail: string
  isComplete: boolean
}

export interface DrivingViolationPayload {
  address: string
  date: string
  is_in_commercial_vehicle: boolean
  violation_type: string
  violation_type_detail: string
}

export function fetchDrivingViolations({
  page = 1,
  userId,
}: {
  page: number
  userId: string
}): Promise<PaginatedResults<DrivingViolation>> {
  const url = `/api/v3/users/${userId}/driving-violations/?page=${page}`
  return axios.get(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return {
        ...response.data,
        results: response.data.results.map(parseDrivingViolation),
      }
    }
  })
}

export function createDrivingViolation(
  userId: string,
  payload: DrivingViolationPayload
): Promise<DrivingViolation> {
  const url = `/api/v3/users/${userId}/driving-violations/`
  return axios.post(url, payload).then((response) => {
    return parseDrivingViolation(response.data)
  })
}

export function updateDrivingViolation(
  userId: string,
  id: string,
  payload: DrivingViolationPayload
): Promise<DrivingViolation> {
  const url = `/api/v3/users/${userId}/driving-violations/${id}/`
  return axios.patch(url, payload).then((response) => {
    return parseDrivingViolation(response.data)
  })
}

export function deleteDrivingViolation(userId: string, id: string) {
  return axios.delete(`/api/v3/users/${userId}/driving-violations/${id}/`)
}

function parseDrivingViolation(data: any) {
  return {
    id: data.id,
    dateCreated: data.date_created,
    dateUpdated: data.date_updated,
    address: data.address,
    date: data.date,
    dateFormatted: data.date_formatted,
    isInCommercialVehicle: data.is_in_commercial_vehicle,
    violationType: data.violation_type,
    violationTypeDetail: data.violation_type_detail,
    isComplete: data.is_complete,
  }
}

export enum AccidentType {
  NON_INJURY = 'non_injury',
  INJURY = 'injury',
  FATALITY = 'fatality',
}

export const AccidentTypeDetail = new Map<AccidentType, string>([
  [AccidentType.NON_INJURY, 'Non Injury'],
  [AccidentType.INJURY, 'Injury'],
  [AccidentType.FATALITY, 'Fatality'],
])

export const getAccidentTypeDetailValues = () =>
  Array.from(AccidentTypeDetail.entries()).map((p) => ({
    label: p[1],
    value: p[0],
  }))

export interface DrivingAccident extends DrivingViolation {
  accidentType: AccidentType
  isDotRecordable: boolean
  isCharged: boolean
}

export interface DrivingAccidentPayload extends DrivingViolationPayload {
  accident_type: string
  is_dot_recordable: boolean
  is_charged: boolean
}

export function fetchDrivingAccidents({
  page = 1,
  userId,
}: {
  page: number
  userId: string
}): Promise<PaginatedResults<DrivingAccident>> {
  const url = `/api/v3/users/${userId}/driving-accidents/?page=${page}`
  return axios.get(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return {
        ...response.data,
        results: response.data.results.map(parseDrivingAccident),
      }
    }
  })
}

export function createDrivingAccident(
  userId: string,
  payload: DrivingAccidentPayload
): Promise<DrivingAccident> {
  const url = `/api/v3/users/${userId}/driving-accidents/`
  return axios.post(url, payload).then((response) => {
    return parseDrivingAccident(response.data)
  })
}

export function updateDrivingAccident(
  userId: string,
  id: string,
  payload: DrivingAccidentPayload
): Promise<DrivingAccident> {
  const url = `/api/v3/${userId}/driving-accidents/${id}/`
  return axios.patch(url, payload).then((response) => {
    return parseDrivingAccident(response.data)
  })
}

export function deleteDrivingAccident(userId: string, id: string) {
  return axios.delete(`/api/v3/users/${userId}/driving-accidents/${id}/`)
}

function parseDrivingAccident(data: any) {
  return {
    ...parseDrivingViolation(data),
    accidentType: data.accident_type,
    isDotRecordable: data.is_dot_recordable,
    isCharged: data.is_charged,
  }
}

export enum EmploymentJobType {
  DRIVING = 'driving',
  NOT_DRIVING = 'not_driving',
}

export enum EmploymentHistoryEquipmentType {
  BUS = 'bus',
  CONVENTIONAL_TRACTOR = 'conventional_tractor',
  TRACTOR_TRAILER = 'tractor_trailer',
  STRAIGHT_TRUCK = 'straight_truck',
}

export enum EmploymentHistoryTrailerType {
  FLATBED = 'flatbed',
  REEFER = 'reefer',
  DRY_VAN = 'dry_van',
  OTHER = 'other',
}

export enum EmploymentHistorySource {
  DRIVER = 'driver',
  EMPLOYER = 'employer',
}

export enum LeavingReason {
  LAID_OFF = 'laid_off',
  TERMINATED = 'terminated',
  PERSONAL_REASONS = 'personal_reasons',
  CHANGED_JOBS = 'moved_companies',
  OTHER = 'other',
}

export enum UnemploymentGapBeforeReason {
  PERSONAL = 'personal',
  FAMILY_EMERGENCY = 'family_emergency',
  VEHICLE_BREAKDOWN = 'vehicle_breakdown',
  EMPLOYER_SHUT_DOWN = 'employer_shut_down',
  FINANCIAL = 'financial',
  OTHER = 'other',
}

export const UnemploymentGapBeforeReasonDetail = new Map<UnemploymentGapBeforeReason, string>([
  [UnemploymentGapBeforeReason.PERSONAL, 'Personal'],
  [UnemploymentGapBeforeReason.FAMILY_EMERGENCY, 'Family emergency'],
  [UnemploymentGapBeforeReason.VEHICLE_BREAKDOWN, 'Vehicle breakdown'],
  [UnemploymentGapBeforeReason.EMPLOYER_SHUT_DOWN, 'Employer shut down'],
  [UnemploymentGapBeforeReason.FINANCIAL, 'Financial'],
  [UnemploymentGapBeforeReason.OTHER, 'Other'],
])

export interface EmploymentHistoryMonthsPayload {
  years: number
}

export interface EmploymentHistoryPayload {
  job_type?: EmploymentJobType
  details: {
    start_date?: string
    end_date?: string | null
    company_address?: string
    dot_number?: string
    is_own_authority?: boolean
    leaving_reason?: LeavingReason | null
    leaving_reason_detail?: string | null
    can_contact_employer?: boolean
    contact_name?: string
    contact_email?: string
    contact_phone?: string
    subject_to_fmcsr_regulations?: boolean
    subject_to_drug_and_alcohol_testing?: boolean
    company_name?: string
    equipment_type?: EmploymentHistoryEquipmentType
    trailer_type?: EmploymentHistoryTrailerType
    source?: EmploymentHistorySource
    job?: any
    unemployment_gap_before_reason?: string
    position_held?: string
  }
}

export type EmploymentJob = {
  id: string
  dateCreated: string
  dateUpdated: string
  user: string
  jobType: EmploymentJobType
  canEdit: boolean | null
  isComplete: boolean | null
  driverDocuments: { filename: string; url: string }[]
  details: {
    id: string
    dateCreated: string
    dateUpdated: string
    source: EmploymentHistorySource
    job?: string
    startDate: string
    startDateObject: Date
    startDateFormatted: string
    endDate: string
    endDateObject: Date | null
    endDateFormatted: string
    companyName: string
    companyAddress: string | null
    dotNumber: string | null
    canContactEmployer: boolean
    leavingReason: LeavingReason | null
    leavingReasonDetail: string | null
    equipmentType: EmploymentHistoryEquipmentType | null
    trailerType: EmploymentHistoryTrailerType | null
    contactName: string | null
    contactEmail: string | null
    contactPhone: string | null
    subjectToFmcsrRegulations: boolean | null
    subjectToDrugAndAlcoholTesting: boolean | null
    isCurrentlyWorkingHere: boolean
    durationInMonths: number
    isInPast3Years: boolean
    isOwnAuthority: boolean
    unemploymentGapBeforeReason: UnemploymentGapBeforeReason
    positionHeld: string
  }
}

export type EmploymentJobResponsePayload = {
  id: string
  date_created: string
  date_updated: string
  user: string
  job_type: EmploymentJobType
  driver_documents: { filename: string; url: string }[]
  can_edit: boolean
  is_complete: boolean
  details: {
    id: string
    date_created: string
    date_updated: string
    source: EmploymentHistorySource
    job?: string
    start_date: string
    start_date_formatted: string
    end_date: string
    end_date_formatted: string
    company_name: string
    company_address: string | null
    dot_number: string | null
    can_contact_employer: boolean
    leaving_reason: LeavingReason | null
    leaving_reason_detail: string | null
    equipment_type: EmploymentHistoryEquipmentType
    trailer_type: EmploymentHistoryTrailerType
    contact_name: string | null
    contact_email: string | null
    contact_phone: string | null
    subject_to_fmcsr_regulations: boolean | null
    subject_to_drug_and_alcohol_testing: boolean | null
    is_currently_working_here: boolean
    duration_in_months: number
    is_in_past_3_years: boolean
    is_own_authority: boolean
    unemployment_gap_before_reason: UnemploymentGapBeforeReason
    position_held: string
  }
}

export function parseEmploymentJob(job: EmploymentJobResponsePayload): EmploymentJob {
  return {
    id: job.id,
    dateCreated: job.date_created,
    dateUpdated: job.date_updated,
    user: job.user,
    jobType: job.job_type,
    driverDocuments: job.driver_documents,
    canEdit: job.can_edit,
    isComplete: job.is_complete,
    details: {
      id: job.details.id,
      dateCreated: job.details.date_created,
      dateUpdated: job.details.date_updated,
      source: job.details.source,
      job: job.details.job,
      startDate: job.details.start_date,
      startDateObject: addNoonToDate(job.details.start_date),
      startDateFormatted: job.details.start_date_formatted,
      endDate: job.details.end_date,
      endDateObject: job.details.end_date ? addNoonToDate(job.details.end_date) : null,
      endDateFormatted: job.details.end_date_formatted,
      companyName: job.details.company_name,
      companyAddress: job.details.company_address,
      dotNumber: job.details.dot_number,
      canContactEmployer: job.details.can_contact_employer,
      leavingReason: job.details.leaving_reason,
      leavingReasonDetail: job.details.leaving_reason_detail,
      equipmentType: job.details.equipment_type,
      trailerType: job.details.trailer_type,
      contactName: job.details.contact_name,
      contactEmail: job.details.contact_email,
      contactPhone: job.details.contact_phone,
      subjectToFmcsrRegulations: job.details.subject_to_fmcsr_regulations,
      subjectToDrugAndAlcoholTesting: job.details.subject_to_drug_and_alcohol_testing,
      isCurrentlyWorkingHere: job.details.is_currently_working_here,
      durationInMonths: job.details.duration_in_months,
      isInPast3Years: job.details.is_in_past_3_years,
      isOwnAuthority: job.details.is_own_authority,
      unemploymentGapBeforeReason: job.details.unemployment_gap_before_reason,
      positionHeld: job.details.position_held,
    },
  }
}

export function fetchEmploymentHistory(userId: string, page = 1): Promise<PaginatedResults<EmploymentJob>> {
  const url = `/api/v3/users/${userId}/employment-history/?page=${page}`
  return axios.get(url).then((response) => {
    return {
      ...response.data,
      results: response.data.results.map((result: EmploymentJobResponsePayload) =>
        parseEmploymentJob(result)
      ),
    }
  })
}

export function fetchEmploymentHistoryItem(userId: string, id: string): Promise<EmploymentJob> {
  const url = `/api/v3/users/${userId}/employment-history/${id}/`
  return axios.get(url).then((response) => {
    return parseEmploymentJob(response.data)
  })
}

export function createEmploymentHistoryItem(
  userId: string,
  payload: EmploymentHistoryPayload
): Promise<EmploymentJob> {
  const url = `/api/v3/users/${userId}/employment-history/`
  return axios.post(url, payload).then((response) => {
    return parseEmploymentJob(response.data)
  })
}

export function updateEmploymentHistoryItem(
  userId: string,
  id: string,
  payload: EmploymentHistoryPayload
): Promise<EmploymentJob> {
  let url = `/api/v3/users/${userId}/employment-history/${id}/`
  return axios.patch(url, payload).then((response) => {
    return parseEmploymentJob(response.data)
  })
}

export function deleteEmploymentHistoryItem(userId: string, id: string) {
  let url = `/api/v3/users/${userId}/employment-history/${id}/`
  return axios.delete(url).then((response) => {
    return response.data
  })
}

export function createEmploymentHistoryDocuments(userId: string, id: string, payload: FormData) {
  let url = `/api/v3/users/${userId}/employment-history/${id}/documents/`
  return axios.post(url, payload).then((response) => {
    return parseEmploymentJob(response.data)
  })
}

export function getEmploymentHistoryMonthsInYears(userId: string, payload: EmploymentHistoryMonthsPayload) {
  let url = `/api/v3/users/${userId}/employment-history/months-employed/`
  return axios.post(url, payload).then((response) => {
    return response.data
  })
}
